'use client';

import * as React from 'react';
import {
  ScatterChart as RechartsScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
} from 'recharts';

// Shadcn chart primitives (`ChartContainer` provides ChartContext — distinct from `ChartFrame`).
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/hen-ui/shadcn/chart';

import { cn, composeRefs, warnOnce } from '@/hen-ui/utils';

import { ChartLegend } from '../ChartLegend';
import {
  useMissingChartConfigWarning,
  renderReferenceLines,
  resolveTooltipValueFormat,
  renderChartTick,
  validateTooltipMode,
  renderTooltipFooter,
  usePinnedTooltip,
  useInteractiveLegend,
  useXAxisConfig,
  sanitizeTicks,
  buildSafeSeriesKeyMap,
  X_AXIS_LABEL_MARGIN_PX,
  type ScatterTooltipPayloadItem,
  resolveScatterTooltipBorderColor,
  downsampleScatterSeries,
} from '../chart-utils';
import {
  withSeriesColorScaleDefaults,
  resolveDivergingArmColor,
} from '../chart-palette';
import {
  evaluateColorRules,
  resolveSeriesBaseColor,
  buildTooltipIndicatorColorResolver,
  withSemanticLegendColors,
} from '../chart-color-rules';
import {
  getDynamicXAxisHeight,
  shouldRotateXAxisLabels,
  getXAxisRightMargin,
} from '@/hen-ui/utils/chart';
import type {
  ReferenceLineConfig,
  SeriesColorConfig,
  ChartTooltipMode,
  ChartTooltipFooter,
  ColorScale,
  SequentialPalette,
  DivergingPalette,
  XAxisInterval,
  ScatterChartDatum,
  ScatterChartSeries,
  ScatterPointShape,
} from '../chart-types';
import {
  resolveTickFormat,
  resolveTooltipFormat,
  resolveValueFormat,
  type TickFormat,
  type TooltipFormat,
  type ValueFormat,
} from '../number-format';

export interface ScatterChartProps {
  /**
   * Single-series form: array of XY (and optional Z) data points. Ignored
   * when `series` is also provided.
   */
  data?: ScatterChartDatum[];
  /**
   * Series identifier used to look up label + color from `config`. Only
   * applies to the single-series form. Defaults to `'default'`.
   */
  seriesKey?: string;
  /**
   * Multi-series form: one entry per group. Each entry renders as its own
   * Recharts `<Scatter>` so the points pick up the series's color, legend
   * entry, and toggle state.
   *
   * Series keys may be **human-readable** (e.g. `"Fall 2026"`): the chart
   * sanitizes them internally into CSS-custom-property-safe tokens for the
   * `--color-<key>` variable and uses the original key as the legend /
   * tooltip label when `config[key].label` is not set.
   *
   * Takes precedence over `data` (and `colorGroupBy`) when both are provided;
   * a one-time dev warn is emitted so the operator notices the ignored prop.
   */
  series?: ScatterChartSeries[];
  /**
   * Split flat `data` into one series per distinct value of this column,
   * keeping the chart's single-series ingestion form on the consumer side.
   * Each group is keyed by the trimmed string value of `row[colorGroupBy]`;
   * null, undefined, and blank values are normalized to the placeholder
   * `'(blank)'`. The chart sanitizes each key into a CSS-safe token
   * internally and uses the normalized value as the series label (so `config`
   * may omit the dynamic group keys).
   *
   * Ignored when `series` is provided (explicit series win). No-op on the
   * single-series path when unset — behavior is unchanged.
   */
  colorGroupBy?: string;
  /** Chart config: maps each series key to label + color (shadcn ChartConfig) */
  config: ChartConfig;
  /** Key in each datum used for the X coordinate */
  xAxisKey: string;
  /** Key in each datum used for the Y coordinate */
  yAxisKey: string;
  /**
   * Optional key in each datum used for the point size (Z dimension). When
   * provided, the chart renders a `<ZAxis>` so points scale within
   * `pointSize` based on this field. When omitted, all points are uniform.
   */
  zAxisKey?: string;
  /** Label displayed alongside the X-axis */
  xAxisLabel?: string;
  /** Label displayed alongside the Y-axis */
  yAxisLabel?: string;
  /** Fixed X-axis range, e.g. [0, 100] */
  xAxisDomain?: [number, number];
  /** Fixed Y-axis range, e.g. [0, 100] */
  yAxisDomain?: [number, number];
  /**
   * Specific tick values on the X-axis.
   *
   * Accepts `number[]` for numeric axes (`xAxisType="number"`, the default)
   * and `string[]` for category axes (`xAxisType="category"`). On a numeric
   * axis the ticks are sanitized locally: non-finite values are dropped
   * (then de-duplicated and sorted ascending) with an `xAxisTicks` console
   * warning in development. Category ticks are forwarded through as-is.
   */
  xAxisTicks?: (number | string)[];
  /** Specific tick values on the Y-axis (always numeric). */
  yAxisTicks?: number[];
  /** Formatter for X-axis tick labels (numeric axis) */
  xAxisFormat?: ValueFormat;
  /** Formatter for Y-axis tick labels */
  yAxisFormat?: ValueFormat;
  /**
   * Formatter for the numeric value rendered in the tooltip. Receives the
   * series name so multi-series charts with heterogeneous units can format
   * per-series. Defaults to `yAxisFormat` if not provided.
   */
  tooltipValueFormat?: (value: number, name: string | number) => string;
  /**
   * Preferred number of ticks on the X-axis. Only meaningful when
   * `xAxisType="number"` (the default for ScatterChart). Values outside
   * [2, 50] are clamped with a console warning.
   *
   * When both `xAxisTickCount` and `xAxisInterval` are set on a numeric
   * axis, tickCount wins (Recharts ignores `interval` on numeric axes
   * anyway). On category axes the wrapper emits a dev-mode warn telling
   * the operator that `tickCount` is being ignored.
   */
  xAxisTickCount?: number;
  /**
   * Controls which X-axis tick labels are shown on category axes. Ignored
   * on numeric axes; setting it there emits a dev-mode warn pointing at
   * `xAxisTickCount`.
   *
   * @see XAxisInterval for the full union (number | 'preserveStart' | …).
   */
  xAxisInterval?: XAxisInterval;
  /** Formatter for X-axis tick labels (any type). */
  xAxisTickFormat?: TickFormat;
  /**
   * When true, shows the X-axis line. When false, hides the axis line while
   * keeping tick marks and labels.
   *
   * @default false (matches existing chart behavior)
   */
  xAxisLineVisible?: boolean;
  /**
   * Recharts X-axis `type`. Scatter is fundamentally a numeric-X chart, so
   * this defaults to `'number'` (the opposite of BarChart / LineChart).
   * Switch to `'category'` only when the X data is a discrete string set
   * (e.g. department names) and you want categorical positioning.
   *
   * @default 'number'
   */
  xAxisType?: 'category' | 'number';
  /**
   * Symbol shape used for all points. For per-series shapes (e.g. circles
   * for series A, triangles for series B) pass `seriesPointShape` instead.
   *
   * @default 'circle'
   */
  pointShape?: ScatterPointShape;
  /**
   * Per-series symbol shape override. When set for a given series key, takes
   * precedence over `pointShape` for that series only. Useful when
   * overlapping clouds need to be told apart at a glance.
   *
   * Keyed by the **public** series key — i.e. the `colorGroupBy` data value
   * (or the `series[].key`) as you pass it in. The chart maps it onto the
   * internal CSS-safe token for you; no pre-sanitizing required.
   */
  seriesPointShape?: Record<string, ScatterPointShape>;
  /**
   * Tuple `[min, max]` controlling point area in px². Forwarded to
   * Recharts' `<ZAxis range>`. When `zAxisKey` is set, points scale within
   * this range based on their Z value; when unset, all points render at
   * `min` (which equals `max` by default).
   *
   * @default [64, 64]
   */
  pointSize?: [number, number];
  /**
   * Cap on the number of points actually rendered, to keep the editor
   * preview responsive (every point is an SVG node). When the incoming valid
   * point count exceeds this, the chart **even-stride downsamples** within
   * each series — `stride = ceil(total / maxPoints)`, keeping every
   * `stride`-th point — so the distribution shape is preserved rather than
   * biasing to the first N points, and every series stays visible. A
   * dev-only `console.warn` notes when downsampling kicks in.
   *
   * @default 5000
   */
  maxPoints?: number;
  /**
   * Chart height in px (default 300).
   *
   * - With `fillContainer={false}` (default): fixed at exactly this many pixels.
   * - With `fillContainer={true}`: acts as the **minimum** height.
   */
  height?: number;
  /** When true, the chart fills its parent container's height. */
  fillContainer?: boolean;
  /** Show legend below chart (default true) */
  showLegend?: boolean;
  /**
   * When `true`, each legend item is an interactive toggle button.
   * Clicking (or pressing Enter / Space) hides / shows that series.
   * Cannot hide the last visible series.
   *
   * Has no effect when `showLegend` is `false` — setting both is a
   * misconfiguration and emits a one-time `console.warn`.
   *
   * @default false
   */
  clickableLegend?: boolean;
  /** Optional reference/target lines drawn on top of the chart. */
  referenceLines?: ReferenceLineConfig[];
  /**
   * Override the default character-count limit (30) used when truncating
   * x-axis labels. Values below 4 are ignored (falls back to the library
   * default).
   */
  xAxisLabelMaxLength?: number;
  /**
   * Per-series color configuration for conditional coloring. Maps series
   * key to its `SeriesColorConfig` (semantic color and/or color rules
   * evaluated against the point's Y value).
   *
   * Keyed by the **public** series key — i.e. the `colorGroupBy` data value
   * (or the `series[].key`) as you pass it in. The chart maps it onto the
   * internal CSS-safe token for you; no pre-sanitizing required.
   */
  seriesColorConfigs?: Record<string, SeriesColorConfig>;
  /**
   * Color-scale mode for the per-series default color (default
   * `'categorical'`). `'sequential'` spreads a light→dark gradient across
   * the visible series; `'diverging'` colors each point by its Y value's
   * sign. Semantic rules (`seriesColorConfigs`) and explicit
   * `config[key].color` still win.
   */
  colorScale?: ColorScale;
  /** Sequential palette family when `colorScale='sequential'` (default `'blues'`). */
  sequentialPalette?: SequentialPalette;
  /** Diverging palette family when `colorScale='diverging'` (default `'blueOrange'`). */
  divergingPalette?: DivergingPalette;
  /**
   * Controls when the data-point tooltip is shown.
   * - `'hover'`    — standard hover tooltip (default).
   * - `'pinned'`   — tooltip appears on click and stays until the user
   *                  clicks elsewhere on the chart or presses Escape.
   * - `'disabled'` — tooltip is suppressed entirely.
   */
  tooltipMode?: ChartTooltipMode;
  /**
   * Row-aware value formatter for the tooltip. Receives the numeric value,
   * the series key, and the full raw data row. Takes precedence over
   * `tooltipValueFormat` when both are provided.
   */
  tooltipFormatter?: TooltipFormat;
  /**
   * Methodology copy rendered as a footer note inside the tooltip bubble.
   * - Plain `string`    — rendered verbatim.
   * - `{ md: string }` — parsed for `**bold**`, `*italic*`, `[text](url)`.
   */
  tooltipContent?: ChartTooltipFooter;
  /** When true, suppresses the tooltip header label; series rows are unaffected */
  hideLabel?: boolean;
}

const EMPTY_FALLBACK_TEXT = 'No data available';
const DEFAULT_SERIES_KEY = 'default';
const DEFAULT_POINT_SIZE: [number, number] = [64, 64];
/**
 * Default cap on rendered points (see {@link ScatterChartProps.maxPoints}).
 * Above this the wrapper even-stride downsamples for render. Tuned a bit
 * higher than {@link LARGE_POINT_COUNT_THRESHOLD} so the soft "consider
 * sampling upstream" nudge fires first, and the hard cap only engages when a
 * set is large enough to actually risk freezing the editor preview.
 */
const DEFAULT_MAX_POINTS = 5_000;
/**
 * Soft warning threshold for point counts. Above this, the wrapper emits a
 * one-time dev warn nudging the operator toward upstream sampling — Recharts
 * can render more, but the editor preview's perceived perf degrades quickly
 * past this point on commodity laptops.
 */
const LARGE_POINT_COUNT_THRESHOLD = 2_000;

// Module-scoped warn de-dupe (reset by `__resetScatterChartWarnings` in tests).
// The both-data-and-series warn lives in the shared `warnOnce` util.
// `warnedLargeN` covers the per-series-list large-point-count warn and
// `warnedDownsample` the "downsampled N→M" notice; both dedupe keys depend on
// the runtime series list and counts.
const warnedLargeN = new Set<string>();
const warnedDownsample = new Set<string>();

/**
 * @internal — test helper only; clears the module-scoped warn-dedup Sets so
 * each test starts from a clean slate. Must live here (not in chart-utils)
 * because `warnedLargeN` and `warnedDownsample` are module-scoped to this
 * file and cannot be reset from outside without this explicit hook.
 *
 * No-op in production builds so accidental imports have no effect.
 */
export const __resetScatterChartWarnings = (): void => {
  if (process.env.NODE_ENV === 'production') return;
  warnedLargeN.clear();
  warnedDownsample.clear();
};

/**
 * Normalize the `data` / `series` ingestion forms into a single
 * `ScatterChartSeries[]` plus the resolved keys. Always returns a non-null
 * array (possibly empty) so the caller can branch on `.length` for the
 * empty-state path.
 *
 * Resolution rules:
 *   - `series` non-empty                  → use as-is; ignore `data`.
 *   - `data` + `colorGroupBy` set         → split flat rows into one series
 *                                            per distinct column value; null /
 *                                            blank values become `'(blank)'`
 *                                            (`grouped`).
 *   - `series` undefined/empty + `data`   → wrap `data` as one entry keyed
 *                                            by `seriesKey` (default
 *                                            `'default'`).
 *   - both `data` and `series`            → `series` wins + one-time warn.
 */
interface NormalizedScatterSeries {
  series: ScatterChartSeries[];
  /**
   * True when `series` was produced by `colorGroupBy` grouping. Grouped keys
   * are raw data values, so the safe-key remap sources each series' label
   * from its key rather than leaving it to the missing-config fallback.
   */
  grouped: boolean;
}

function normalizeSeries(
  data: ScatterChartDatum[] | undefined,
  seriesKey: string,
  series: ScatterChartSeries[] | undefined,
  colorGroupBy: string | undefined,
): NormalizedScatterSeries {
  const hasMultiSeries = Array.isArray(series) && series.length > 0;
  const hasSingleData = Array.isArray(data) && data.length > 0;

  if (
    process.env.NODE_ENV !== 'production' &&
    hasMultiSeries &&
    hasSingleData
  ) {
    warnOnce(
      'ScatterChart/both-data-and-series',
      `[hen-ui/ScatterChart] both \`data\` and \`series\` were provided — \`series\` wins and \`data\` is ignored. Drop one to silence this warning.`,
    );
  }

  // Explicit series always win — `colorGroupBy` only shapes the flat `data`
  // form, and `data` is ignored when `series` is present.
  if (hasMultiSeries) return { series, grouped: false };

  if (hasSingleData) {
    const groupColumn =
      typeof colorGroupBy === 'string' && colorGroupBy.trim() !== ''
        ? colorGroupBy
        : undefined;
    if (groupColumn) {
      // Split flat rows into one series per distinct column value, preserving
      // first-seen order. Keys are the raw values (e.g. "Fall 2026"), with
      // null / undefined / blank values normalized to the literal string
      // "(blank)" so the legend label is never empty and the CSS token is
      // meaningful. The safe-key remap sanitizes them for the `--color-<key>`
      // variable and keeps the normalized key as the legend label.
      const groups = new Map<string, ScatterChartDatum[]>();
      for (const row of data) {
        // Normalize null / undefined / blank strings to a readable placeholder
        // so the legend label is never empty and the CSS token is meaningful
        // (`toCssVarSafeKey('') → 'series'` which is not human-readable).
        const raw = String(row[groupColumn] ?? '').trim();
        const value = raw !== '' ? raw : '(blank)';
        const bucket = groups.get(value);
        if (bucket) bucket.push(row);
        else groups.set(value, [row]);
      }
      const groupedSeries = Array.from(groups, ([key, rows]) => ({
        key,
        data: rows,
      }));
      return { series: groupedSeries, grouped: true };
    }
    return { series: [{ key: seriesKey, data }], grouped: false };
  }

  return { series: [], grouped: false };
}

/** Re-key a public-keyed override record (`seriesColorConfigs`,
 * `seriesPointShape`) onto the internal CSS-safe tokens. Entries whose key
 * isn't in `keyMap` (not a series key) pass through untouched and are
 * harmlessly ignored downstream. Returns `undefined` for `undefined` input. */
function remapRecordKeys<T>(
  record: Record<string, T> | undefined,
  keyMap: Map<string, string>,
): Record<string, T> | undefined {
  if (!record) return undefined;
  const next: Record<string, T> = {};
  for (const [rawKey, value] of Object.entries(record)) {
    next[keyMap.get(rawKey) ?? rawKey] = value;
  }
  return next;
}

/**
 * Re-key the normalized series + their config / per-series overrides onto
 * CSS-custom-property-safe tokens so `--color-<key>` is always a valid
 * declaration, while the public API stays human-readable.
 *
 * For each series the label is resolved as: the consumer's explicit
 * `config[rawKey].label`, else — when the series came from grouping OR its
 * key had to be sanitized — the raw key itself (so the legend/tooltip never
 * surface the mangled token). Already-safe keys with no config entry are left
 * out of the returned config so the dev-only missing-config warning still
 * fires for genuine drift, exactly as before.
 */
interface SafeSeriesView {
  series: ScatterChartSeries[];
  config: ChartConfig;
  seriesColorConfigs?: Record<string, SeriesColorConfig>;
  seriesPointShape?: Record<string, ScatterPointShape>;
}

function buildSafeSeriesView(
  normalizedSeries: ScatterChartSeries[],
  grouped: boolean,
  config: ChartConfig,
  seriesColorConfigs: Record<string, SeriesColorConfig> | undefined,
  seriesPointShape: Record<string, ScatterPointShape> | undefined,
): SafeSeriesView {
  const rawKeys = normalizedSeries.map((s) => s.key);
  const keyMap = buildSafeSeriesKeyMap(rawKeys);

  const series = normalizedSeries.map((s) => ({
    key: keyMap.get(s.key) ?? s.key,
    data: s.data,
  }));

  const safeConfig: ChartConfig = {};
  for (const rawKey of rawKeys) {
    const safeKey = keyMap.get(rawKey) ?? rawKey;
    const explicit = config[rawKey];
    // Guard: if rawKey is blank/whitespace (e.g. series={[{ key: '' }]}),
    // `toCssVarSafeKey` returns the generic fallback `'series'` which is not
    // equal to rawKey, so the label-fallback branch would fire and emit an
    // empty string as the legend/tooltip label.  Substitute the same
    // placeholder used by the colorGroupBy grouping path so the label is
    // always at least human-readable.
    const label =
      explicit?.label ??
      (grouped || safeKey !== rawKey ? rawKey.trim() || '(blank)' : undefined);
    if (explicit !== undefined || label !== undefined) {
      safeConfig[safeKey] =
        label !== undefined ? { ...explicit, label } : { ...explicit };
    }
  }

  return {
    series,
    config: safeConfig,
    seriesColorConfigs: remapRecordKeys(seriesColorConfigs, keyMap),
    seriesPointShape: remapRecordKeys(seriesPointShape, keyMap),
  };
}

/**
 * Returns true when `v` is a string consisting entirely of whitespace.
 * Extracted to module scope so `coercePointValues` doesn't allocate a new
 * function object on every row.
 */
const isBlankString = (v: unknown): boolean =>
  typeof v === 'string' && v.trim() === '';

/**
 * Coerce a row's `xAxisKey` / `yAxisKey` values to numbers. Returns `null`
 * for either field when the raw value is `null`, `undefined`, or coerces
 * to `NaN`. Categorical-X is supported by returning the raw string value
 * for X while still requiring numeric Y.
 *
 * Empty / whitespace-only strings are dropped explicitly because the native
 * `Number('')` / `Number('   ')` / `Number('\t')` quirk returns `0` (which
 * `Number.isFinite` then accepts), silently plotting blank cells at the
 * origin. We treat those as missing data instead.
 */
function coercePointValues(
  row: ScatterChartDatum,
  xKey: string,
  yKey: string,
  xAxisType: 'category' | 'number',
): { x: number | string | null; y: number | null } {
  const rawX = row[xKey];
  const rawY = row[yKey];

  const x =
    xAxisType === 'category'
      ? rawX == null || isBlankString(rawX)
        ? null
        : String(rawX)
      : rawX == null ||
          (typeof rawX !== 'number' && typeof rawX !== 'string') ||
          isBlankString(rawX)
        ? null
        : Number.isFinite(Number(rawX))
          ? Number(rawX)
          : null;

  const y =
    rawY == null || isBlankString(rawY)
      ? null
      : Number.isFinite(Number(rawY))
        ? Number(rawY)
        : null;

  return { x, y };
}

/**
 * ScatterChart — hen-ui composite wrapping Recharts `<ScatterChart>`. Renders
 * one symbol per data point on a numeric X / numeric Y plane (the most common
 * scatter shape). Supports multi-series via the `series[]` prop, per-series
 * color rules, per-point Z-driven sizing, and the standard hen-ui axis /
 * tooltip / legend / reference-line surface.
 *
 * Data ingestion is flexible: pass a single `data` array, an explicit
 * `series[]` list, or flat `data` + `colorGroupBy` to split rows into one
 * series per distinct column value. Series keys may be human-readable
 * (e.g. `"Fall 2026"`) — the chart sanitizes them into CSS-safe `--color-`
 * tokens internally and keeps the raw value as the legend / tooltip label.
 * Very large point sets are even-stride downsampled to `maxPoints` so the
 * editor preview stays responsive, and the legend defaults on only when more
 * than one series is resolved.
 *
 * Stateless, prop-driven, theme-aware via ChartConfig + scoped `--color-<key>`
 * CSS variables. Mirrors BarChart's empty-state, sizing, and accessibility
 * contracts so dashboard authors get identical behavior across chart types.
 */
export const ScatterChart = React.forwardRef<HTMLDivElement, ScatterChartProps>(
  (
    {
      data,
      seriesKey = DEFAULT_SERIES_KEY,
      series: rawSeries,
      colorGroupBy,
      config,
      xAxisKey,
      yAxisKey,
      zAxisKey,
      xAxisLabel,
      yAxisLabel,
      xAxisDomain,
      yAxisDomain,
      xAxisTicks: rawXAxisTicks,
      yAxisTicks: rawYAxisTicks,
      xAxisFormat: rawXAxisFormat,
      yAxisFormat: rawYAxisFormat,
      tooltipValueFormat,
      xAxisTickCount: rawXAxisTickCount,
      xAxisInterval: rawXAxisInterval,
      xAxisTickFormat: rawXAxisTickFormat,
      xAxisLineVisible = false,
      xAxisType = 'number',
      pointShape = 'circle',
      seriesPointShape,
      pointSize = DEFAULT_POINT_SIZE,
      maxPoints = DEFAULT_MAX_POINTS,
      height = 300,
      fillContainer = false,
      showLegend: showLegendProp,
      clickableLegend = false,
      referenceLines,
      xAxisLabelMaxLength,
      seriesColorConfigs,
      colorScale = 'categorical',
      sequentialPalette,
      divergingPalette,
      tooltipMode,
      tooltipFormatter: rawTooltipFormatter,
      tooltipContent,
      hideLabel,
    },
    ref,
  ) => {
    const xAxisFormat = React.useMemo(
      () => resolveValueFormat(rawXAxisFormat),
      [rawXAxisFormat],
    );
    const yAxisFormat = React.useMemo(
      () => resolveValueFormat(rawYAxisFormat),
      [rawYAxisFormat],
    );
    const xAxisTickFormat = React.useMemo(
      () => resolveTickFormat(rawXAxisTickFormat),
      [rawXAxisTickFormat],
    );
    const tooltipFormatter = React.useMemo(
      () => resolveTooltipFormat(rawTooltipFormatter, yAxisFormat),
      [rawTooltipFormatter, yAxisFormat],
    );

    // ── Sizing & guards ──────────────────────────────────────────────────────
    const sizingStyle: React.CSSProperties = fillContainer
      ? { minHeight: height }
      : { height };
    const sizingClass = fillContainer ? 'h-full' : undefined;

    // Treat empty / whitespace-only axis keys as "missing metric": a scatter
    // needs both X and Y, so an operator who forgets to pick a metric in the
    // editor surfaces that at runtime as an empty key string.
    const hasXKey = typeof xAxisKey === 'string' && xAxisKey.trim() !== '';
    const hasYKey = typeof yAxisKey === 'string' && yAxisKey.trim() !== '';
    const hasZKey = typeof zAxisKey === 'string' && zAxisKey.trim() !== '';
    const missingMetric = !hasXKey || !hasYKey;

    // ── Normalize ingestion ──────────────────────────────────────────────────
    const { series: normalizedSeries, grouped } = React.useMemo(
      () => normalizeSeries(data, seriesKey, rawSeries, colorGroupBy),
      [data, seriesKey, rawSeries, colorGroupBy],
    );

    // ── CSS-safe series keys ─────────────────────────────────────────────────
    // Re-key series / config / per-series overrides onto CSS-safe tokens so
    // `--color-<key>` is always a valid declaration even when the public key is
    // a human-readable group value ("Fall 2026"). The original key stays the
    // legend / tooltip label.
    const safeView = React.useMemo(
      () =>
        buildSafeSeriesView(
          normalizedSeries,
          grouped,
          config,
          seriesColorConfigs,
          seriesPointShape,
        ),
      [normalizedSeries, grouped, config, seriesColorConfigs, seriesPointShape],
    );
    const safeSeries = safeView.series;
    const safeConfig = safeView.config;
    const safeSeriesColorConfigs = safeView.seriesColorConfigs;
    const safeSeriesPointShape = safeView.seriesPointShape;

    const seriesKeys = React.useMemo(
      () => safeSeries.map((s) => s.key),
      [safeSeries],
    );

    // ── Legend default ───────────────────────────────────────────────────────
    // A one-item legend is noise, so default the legend ON only when the
    // resolved series count is > 1; a single series hides it. An explicit
    // `showLegend` always wins.
    const showLegend = showLegendProp ?? seriesKeys.length > 1;

    // ── Drop invalid rows ────────────────────────────────────────────────────
    const sanitizedSeries = React.useMemo(() => {
      // When the operator forgot to set a metric the missing-metric fallback
      // takes over below — short-circuit so we don't pay the per-row coerce
      // cost for unreachable data.
      if (!hasXKey || !hasYKey) return [];

      return safeSeries.map((s) => {
        const kept: ScatterChartDatum[] = [];
        for (const row of s.data) {
          const coerced = coercePointValues(row, xAxisKey, yAxisKey, xAxisType);
          if (coerced.x == null || coerced.y == null) continue;
          kept.push({
            ...row,
            [xAxisKey]: coerced.x,
            [yAxisKey]: coerced.y,
          });
        }
        return { key: s.key, data: kept };
      });
    }, [safeSeries, xAxisKey, yAxisKey, xAxisType, hasXKey, hasYKey]);

    // Pre-downsample valid-point total — drives the empty state and the soft
    // large-N warn (the operator cares about the real data size, not the
    // rendered sample).
    const totalPoints = React.useMemo(
      () => sanitizedSeries.reduce((sum, s) => sum + s.data.length, 0),
      [sanitizedSeries],
    );

    // ── Downsample for render (keep the editor preview responsive) ───────────
    const displaySeries = React.useMemo(
      () => downsampleScatterSeries(sanitizedSeries, totalPoints, maxPoints),
      [sanitizedSeries, totalPoints, maxPoints],
    );
    const displayedPoints = React.useMemo(
      () => displaySeries.reduce((sum, s) => sum + s.data.length, 0),
      [displaySeries],
    );

    // Soft warn: large data set the operator may want to sample upstream.
    React.useEffect(() => {
      if (process.env.NODE_ENV === 'production') return;
      if (totalPoints <= LARGE_POINT_COUNT_THRESHOLD) return;
      const warnKey = `large-${seriesKeys.join('|')}-${totalPoints}`;
      if (warnedLargeN.has(warnKey)) return;
      warnedLargeN.add(warnKey);
      console.warn(
        `[hen-ui/ScatterChart] ${totalPoints} points rendered (threshold: ${LARGE_POINT_COUNT_THRESHOLD}). Consider sampling upstream for smoother editor preview performance.`,
      );
    }, [totalPoints, seriesKeys]);

    // Notice when the hard cap actually downsampled, so the gap between the
    // data size and the rendered sample is discoverable in dev.
    React.useEffect(() => {
      if (process.env.NODE_ENV === 'production') return;
      if (displayedPoints >= totalPoints) return;
      const warnKey = `downsample-${seriesKeys.join('|')}-${totalPoints}-${displayedPoints}`;
      if (warnedDownsample.has(warnKey)) return;
      warnedDownsample.add(warnKey);
      console.warn(
        `[hen-ui/ScatterChart] downsampled ${totalPoints} points → ${displayedPoints} for rendering (maxPoints=${maxPoints}). Raise \`maxPoints\` or sample upstream to change this.`,
      );
    }, [displayedPoints, totalPoints, maxPoints, seriesKeys]);

    // ── Tick hygiene (reuse the shared helper used by Bar/Line/Combo) ────────
    const yAxisTicks = React.useMemo(
      () => sanitizeTicks(rawYAxisTicks, 'ScatterChart'),
      [rawYAxisTicks],
    );
    const xAxisTicks = React.useMemo(() => {
      if (xAxisType === 'category') {
        // Category axes use string (or numeric) values as tick identifiers —
        // clone the input to prevent Recharts from mutating a frozen or
        // read-only array (Storybook args, Redux state, Object.freeze, etc.).
        return Array.isArray(rawXAxisTicks) && rawXAxisTicks.length > 0
          ? [...rawXAxisTicks]
          : undefined;
      }
      // Numeric axis: sanitize locally so the warn message references
      // "xAxisTicks" — sanitizeTicks always emits "yAxisTicks[...]" which
      // would mislead the operator into looking at the wrong prop.
      if (!Array.isArray(rawXAxisTicks) || rawXAxisTicks.length === 0)
        return undefined;
      const cleaned: number[] = [];
      for (const t of rawXAxisTicks) {
        const n = Number(t);
        if (!Number.isFinite(n)) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn(
              `[hen-ui/ScatterChart] xAxisTicks contains a non-finite value (${String(t)}) — it will be ignored.`,
            );
          }
          continue;
        }
        if (!cleaned.includes(n)) cleaned.push(n);
      }
      return cleaned.length > 0 ? cleaned.sort((a, b) => a - b) : undefined;
    }, [rawXAxisTicks, xAxisType]);

    // ── Missing-config warn ──────────────────────────────────────────────────
    useMissingChartConfigWarning('ScatterChart', safeConfig, seriesKeys);

    // ── X-axis resolver (clamp / validate / warn for tickCount + interval) ──
    const xAxisConfig = useXAxisConfig({
      rawTickCount: rawXAxisTickCount,
      rawInterval: rawXAxisInterval,
      xAxisType,
      chartName: 'ScatterChart',
    });

    // ── Interactive legend + per-series color resolution ────────────────────
    const { hiddenSeries, handleSeriesToggle } = useInteractiveLegend({
      series: seriesKeys,
      showLegend,
      clickableLegend,
      chartName: 'ScatterChart',
    });

    const resolvedConfig = React.useMemo(
      () =>
        withSeriesColorScaleDefaults(safeConfig, seriesKeys, {
          colorScale,
          sequentialPalette,
          divergingPalette,
          hiddenSeries,
        }),
      [
        safeConfig,
        seriesKeys,
        colorScale,
        sequentialPalette,
        divergingPalette,
        hiddenSeries,
      ],
    );

    const legendConfig = React.useMemo(
      () =>
        withSemanticLegendColors(
          resolvedConfig,
          safeSeriesColorConfigs,
          seriesKeys,
        ),
      [resolvedConfig, safeSeriesColorConfigs, seriesKeys],
    );

    // ── Tooltip / pinning ────────────────────────────────────────────────────
    const resolvedTooltipMode = validateTooltipMode(tooltipMode);
    const {
      tooltipActive,
      onChartClick,
      containerRef: pinnedRef,
    } = usePinnedTooltip(resolvedTooltipMode);
    const rootRef = React.useMemo(
      () => composeRefs<HTMLDivElement>(pinnedRef, ref),
      [pinnedRef, ref],
    );

    // ── Tooltip content ─────────────────────────────────────────────────────
    // Scatter tooltips suppress the per-row color swatch (every row in the
    // bubble belongs to the same point of the same series, so the swatch
    // never disambiguates anything) and instead carry the active series's
    // color on the bubble's outline. The resolver mirrors the same
    // priority `ChartTooltipContent` uses internally for indicator color so
    // per-point `colorRules` / diverging-arm fills stay reflected.
    const indicatorColorResolver = React.useMemo(
      () => buildTooltipIndicatorColorResolver(safeSeriesColorConfigs),
      [safeSeriesColorConfigs],
    );

    const resolveTooltipBorderColor = React.useCallback(
      (
        payload: ReadonlyArray<ScatterTooltipPayloadItem> | undefined,
      ): string | undefined =>
        resolveScatterTooltipBorderColor(payload, indicatorColorResolver),
      [indicatorColorResolver],
    );

    // ── X-axis label rows for rotation / height / margin heuristics ─────────
    // `shouldRotateXAxisLabels`, `getDynamicXAxisHeight`, and
    // `getXAxisRightMargin` only read label text — not Y values or series
    // structure. Source them from the pre-downsample `sanitizedSeries`,
    // deduplicated by X value, so:
    //   • The longest label is never missed because the even-stride sampler
    //     dropped the rows that carried it.
    //   • `shouldRotateXAxisLabels` sees the true distinct-category count
    //     (e.g. 8), not the raw point count (e.g. 4 000) or the downsampled
    //     subset — `data.length` is its proxy for label density on the axis.
    const xLabelRows = React.useMemo(() => {
      const seen = new Set<string>();
      const rows: ScatterChartDatum[] = [];
      for (const s of sanitizedSeries) {
        for (const row of s.data) {
          const xVal = String(row[xAxisKey] ?? '');
          if (seen.has(xVal)) continue;
          seen.add(xVal);
          rows.push(row);
        }
      }
      return rows;
    }, [sanitizedSeries, xAxisKey]);

    const needsRotation =
      xAxisType === 'category' && shouldRotateXAxisLabels(xLabelRows, xAxisKey);
    const xAxisHeight = needsRotation
      ? getDynamicXAxisHeight(xLabelRows, xAxisKey, !!xAxisLabel)
      : xAxisLabel
        ? 80
        : 60;

    // ── Early returns: missing-metric and empty states ───────────────────────
    if (missingMetric) {
      const missingMetricMessage =
        !hasXKey && !hasYKey
          ? 'Add X and Y metrics to see data'
          : !hasXKey
            ? 'Add an X axis metric to see data'
            : 'Add a Y axis metric to see data';
      return (
        <div
          ref={ref}
          aria-label={missingMetricMessage}
          className={cn(
            'flex items-center justify-center rounded bg-neutral-50 text-neutral-500 text-b4',
            sizingClass,
          )}
          style={sizingStyle}
        >
          {missingMetricMessage}
        </div>
      );
    }

    if (totalPoints === 0) {
      return (
        <div
          ref={ref}
          aria-label={EMPTY_FALLBACK_TEXT}
          className={cn(
            'flex items-center justify-center rounded bg-neutral-50 text-neutral-500 text-b4',
            sizingClass,
          )}
          style={sizingStyle}
        >
          {EMPTY_FALLBACK_TEXT}
        </div>
      );
    }

    // ── Render Scatter elements per series ───────────────────────────────────
    const renderScatters = () =>
      displaySeries
        .filter((s) => !hiddenSeries.has(s.key))
        .map((s) => {
          const cfg = safeSeriesColorConfigs?.[s.key];
          const baseColor = resolveSeriesBaseColor(
            s.key,
            safeSeriesColorConfigs,
          );
          const shape = safeSeriesPointShape?.[s.key] ?? pointShape;

          // Per-point fills only when colorRules or semantic 'auto-by-value' are
          // active. Without those, the single `fill` prop is enough — no need
          // to allocate one resolved color per point. Recharts' Scatter reads
          // a top-level `fill` field on each data row and uses it as the
          // per-point color (no `<Cell>` children needed — that pattern is
          // BarChart-specific because Bar receives data from the parent
          // ChartContainer, not directly).
          const needsPerPointFill =
            !!cfg?.colorRules?.length ||
            cfg?.semanticColor === 'auto-by-value' ||
            colorScale === 'diverging';

          const pointsWithFill = needsPerPointFill
            ? s.data.map((row) => {
                const yValue =
                  typeof row[yAxisKey] === 'number'
                    ? (row[yAxisKey] as number)
                    : null;
                // Diverging colors each point by its Y value's sign (high arm
                // for positive, low arm for negative). When the palette
                // resolver returns null (no diverging or no value), fall back
                // to the per-series CSS var. Semantic colorRules then
                // evaluate on top of that resolved default.
                const defaultColor =
                  colorScale === 'diverging'
                    ? (resolveDivergingArmColor(yValue, divergingPalette) ??
                      `var(--color-${s.key})`)
                    : `var(--color-${s.key})`;
                return {
                  ...row,
                  fill: evaluateColorRules(
                    cfg?.colorRules,
                    yValue,
                    cfg?.semanticColor,
                    defaultColor,
                  ),
                };
              })
            : s.data;

          return (
            <Scatter
              key={s.key}
              name={s.key}
              data={pointsWithFill}
              fill={baseColor}
              shape={shape}
            />
          );
        });

    return (
      <div
        ref={rootRef}
        role='figure'
        aria-label='Scatter chart'
        className={cn('flex flex-col', sizingClass)}
        style={sizingStyle}
      >
        <ChartContainer
          config={resolvedConfig}
          className='aspect-auto flex-1 w-full'
        >
          <RechartsScatterChart
            accessibilityLayer
            onClick={onChartClick}
            margin={{
              right: getXAxisRightMargin(xLabelRows, xAxisKey, needsRotation),
              bottom: !showLegend && xAxisLabel ? X_AXIS_LABEL_MARGIN_PX : 0,
            }}
          >
            <CartesianGrid strokeDasharray='3 3' />

            <XAxis
              type={xAxisConfig.type}
              dataKey={xAxisKey}
              domain={
                xAxisDomain
                  ? ([...xAxisDomain] as [number, number])
                  : xAxisConfig.domain
              }
              ticks={xAxisTicks}
              height={xAxisHeight}
              tickCount={xAxisConfig.tickCount}
              interval={xAxisConfig.interval}
              tickFormatter={
                xAxisTickFormat ??
                (xAxisFormat
                  ? (v: number | string) =>
                      typeof v === 'number' ? xAxisFormat(v) : String(v)
                  : undefined)
              }
              tick={(p) =>
                renderChartTick({
                  ...p,
                  rotate: needsRotation,
                  textAnchor: needsRotation ? 'end' : 'middle',
                  maxLabelLength: xAxisLabelMaxLength,
                  tickFormatter: xAxisTickFormat,
                })
              }
              label={
                xAxisLabel
                  ? {
                      value: xAxisLabel,
                      position: 'insideBottom',
                      offset: -5,
                    }
                  : undefined
              }
              tickLine={false}
              axisLine={xAxisLineVisible}
            />

            <YAxis
              type='number'
              dataKey={yAxisKey}
              domain={
                yAxisDomain ? ([...yAxisDomain] as [number, number]) : undefined
              }
              ticks={yAxisTicks}
              tickFormatter={yAxisFormat}
              label={
                yAxisLabel
                  ? {
                      value: yAxisLabel,
                      angle: -90,
                      position: 'insideLeft',
                      style: { textAnchor: 'middle' },
                    }
                  : undefined
              }
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={false}
            />

            {hasZKey && (
              <ZAxis
                type='number'
                dataKey={zAxisKey}
                range={[...pointSize] as [number, number]}
                name='size'
              />
            )}

            {resolvedTooltipMode !== 'disabled' && (
              <ChartTooltip
                cursor={{ strokeDasharray: '3 3' }}
                active={tooltipActive}
                trigger={resolvedTooltipMode === 'pinned' ? 'click' : undefined}
                content={(contentProps) => {
                  // Recharts injects `active`, `payload`, `label` (plus chart-
                  // internal coordinate / viewBox props we deliberately ignore
                  // — passing them through would leak onto the bubble's
                  // `<div>` and trigger React DOM warnings).
                  const { active, payload, label } = contentProps;
                  const borderColor = resolveTooltipBorderColor(
                    payload as
                      | ReadonlyArray<ScatterTooltipPayloadItem>
                      | undefined,
                  );
                  return (
                    <ChartTooltipContent
                      active={active}
                      payload={payload}
                      label={label}
                      hideIndicator
                      hideLabel={hideLabel}
                      borderColor={borderColor}
                      valueFormatter={
                        tooltipFormatter
                          ? undefined
                          : resolveTooltipValueFormat(
                              tooltipValueFormat,
                              yAxisFormat,
                            )
                      }
                      rowValueFormatter={tooltipFormatter}
                      footer={renderTooltipFooter(tooltipContent)}
                    />
                  );
                }}
              />
            )}

            {showLegend && (
              <ChartLegend
                series={seriesKeys}
                config={legendConfig}
                seriesColorConfigs={safeSeriesColorConfigs}
                colorScale={colorScale}
                divergingPalette={divergingPalette}
                clickableLegend={clickableLegend}
                hiddenSeries={hiddenSeries}
                onSeriesToggle={handleSeriesToggle}
                verticalAlign='bottom'
                align='center'
                wrapperStyle={xAxisLabel ? { paddingTop: 12 } : undefined}
              />
            )}

            {renderScatters()}
            {renderReferenceLines(referenceLines, 'vertical')}
          </RechartsScatterChart>
        </ChartContainer>
      </div>
    );
  },
);
ScatterChart.displayName = 'ScatterChart';
